import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Navbar from "@components/layout/Navbar";
import SuccessModal from "@components/common/SuccessModal";
import FailureModal from "@components/common/FailureModal";
import FormHeader from "@components/form-builder/FormHeader";
import FormBody from "@components/form-builder/FormBody";
import AISummaryBox from "@components/form-builder/AISummaryBox";
import StickyFooter from "@components/form-builder/StickyFooter";
import { injectDraftSignatures } from "@utils/formUtils";
import { saveFile } from "@services/file";
import { getAISummary } from "@services/aiSummary";
import {
  verifyTokenAndFetchTemplate,
  savePatientFile,
} from "@services/PatientAccess";
import { diffForm } from "@utils/formDiffUtils";
import { sanitizeFormValues } from "@utils/sanitize";
export default function FillForm({ isPatientAccess = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signatureRefs = useRef({});
  const lastScrollTop = useRef(0);

  const formName = location.state?.formName || "Untitled Form";
  const id = location.state?.id || null;
  const name = location.state?.name || null;
  const structure =
    location.state?.structure || location.state?.contant || null;
  const content = location.state?.content || null;
  const patientName = location.state?.patient_name || "Unknown";
  const template_id = location.state?.template_id || null;
  const patient_id = location.state?.patient_id || null;
  const isNewFile = !id;

  const [formData, setFormData] = useState(() => {
    if (id && content) return JSON.parse(content);
    if (structure) return JSON.parse(structure);
    return [];
  });
  const initialFormDataRef = useRef(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [draftSignatures, setDraftSignatures] = useState({});
  const [isSticky, setIsSticky] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState("");
  const [isVerified, setIsVerified] = useState(() => !isPatientAccess);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const clone = (obj) => JSON.parse(JSON.stringify(obj ?? null));

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (currentScroll < lastScrollTop.current - 10) {
        setIsSticky(true);
      } else if (currentScroll > lastScrollTop.current + 10) {
        setIsSticky(false);
      }
      lastScrollTop.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isPatientAccess) {
      const params = Object.fromEntries(searchParams.entries());
      verifyTokenAndFetchTemplate(params)
        .then((templateData) => {
          setFormData(templateData.structure);
          setIsVerified(true);
          // set baseline once for patient access
          if (!initialFormDataRef.current) {
            initialFormDataRef.current = clone(templateData.structure);
          }
        })
        .catch((err) => {
          console.error(err);
          setError("This form link is invalid or has expired.");
        });
    }
  }, [isPatientAccess, searchParams]);
  // set baseline once for staff access (when coming from state)
  useEffect(() => {
    if (!isPatientAccess && formData?.length && !initialFormDataRef.current) {
      initialFormDataRef.current = clone(formData);
    }
  }, [isPatientAccess, formData]);


  if (isPatientAccess && !isVerified) {
    return <div className="text-center text-red-600 p-10 font-medium">{error}</div>;
  }

  const handleNestedChange = (index, field, value, optionIndex = null) => {
    setFormData((prev) => {
      const updated = [...prev];
      const question = updated[activeTabIndex].content[index];
      if (field === "answer") {
        question.answer = value;
      } else if (field === "notes" && optionIndex !== null) {
        question.options = question.options.map((opt) =>
          opt.index === optionIndex ? { ...opt, notes: value } : opt
        );
      }
      return updated;
    });
  };

  const handleSave = async () => {
    const updated = injectDraftSignatures(formData, draftSignatures);
    // NEW: compute changed fields vs. initial snapshot

    const baseline =
      initialFormDataRef.current || JSON.parse(JSON.stringify(updated));
    const { count, changes } = diffForm(baseline, updated);
    console.log(
      `[Template ${template_id ?? "unknown"}] changed_fields_count=${count}`
    );
    console.table(
      changes.map((c) => ({
        tab: c.tabIndex,
        qIdx: c.questionIndex,
        qId: c.questionId,
        type: c.type,
        before: c.before.answer ?? c.before.value,
        after: c.after.answer ?? c.after.value,
      }))
    );

    if (aiSummary.trim()) {
      const lastTab = updated.at(-1);
      const lastId = parseInt(lastTab.content.at(-1)?.id || 0) + 1;
      lastTab.content.push({
        id: String(lastId),
        select: "Paragraph",
        question: "Summary",
        value: "",
        answer: aiSummary,
        required: false,
        isParagraphAnswer: true,
        isListMode: false,
      });
    }

    const payload = {
      id: id || null,
      name: name || `${patientName}-${formName}`,
      template_id: template_id,
      template_name: formName,
      patient_id: patient_id,
      content: updated,
      changed_fields_count: count,
    };

    try {
      await saveFile(sanitizeFormValues(payload));
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate(-1);
      }, 3000);
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong.");
      setShowFailure(true);
      setTimeout(() => setShowFailure(false), 4000);
    }
  };

  const handleSendAsPatient = async () => {
    setIsSubmitting(true);

    const payload = {
      token: searchParams.get("token"),
      user_id: searchParams.get("user_id"),
      template_id: searchParams.get("template_id"),
      patient_id: searchParams.get("patient_id"),
      name: `${searchParams.get("name")}-${searchParams.get("form_name")}`,
      patient_name: searchParams.get("name"),
      template_name: searchParams.get("form_name"),
      content: injectDraftSignatures(formData, draftSignatures),
    };

    try {
      await savePatientFile(sanitizeFormValues(payload));
      setShowSuccess(true);
      setTimeout(() => navigate("/thank-you"), 3000);
    } catch (err) {
      setErrorMessage(err.message || "Submission failed.");
      setShowFailure(true);
       setTimeout(() => setShowFailure(false), 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateAISummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const response = await getAISummary({
        content: injectDraftSignatures(formData, draftSignatures),
      });
      setAiSummary(response.ai_summary);
    } catch (err) {
      setErrorMessage(err.message || "AI Summary generation failed.");
      setShowFailure(true);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="sticky top-0 z-50">
        <Navbar showFieldSearch={false} />
      </div>
      <div className="p-5 flex-grow">
        <FormHeader
          title={searchParams.get("form_name") || formName}
          isSticky={isSticky}
          tabs={formData}
          activeTabIndex={activeTabIndex}
          setActiveTabIndex={setActiveTabIndex}
        />

        <FormBody
          id={id}
          formData={formData}
          activeTabIndex={activeTabIndex}
          handleChange={handleNestedChange}
          signatureRefs={signatureRefs}
          draftSignatures={draftSignatures}
          setDraftSignatures={setDraftSignatures}
        />

        {!isPatientAccess &&
          isNewFile &&
          activeTabIndex === formData.length - 1 && (
            <AISummaryBox
              aiSummary={aiSummary}
              setAiSummary={setAiSummary}
              isGenerating={isGeneratingSummary}
              onGenerate={handleGenerateAISummary}
            />
          )}
      </div>

      <StickyFooter
        isPatientAccess={isPatientAccess}
        isSubmitting={isSubmitting}
        onSubmit={isPatientAccess ? handleSendAsPatient : handleSave}
      />

      <SuccessModal
        isOpen={showSuccess}
        title={id ? "File updated successfully" : "File created successfully"}
        message="Your file has been saved successfully."
      />
      <FailureModal
        isOpen={showFailure}
        titleError="Submission failed"
        errorMessage={errorMessage}
      />
    </div>
  );
}
